import Cookies from 'js-cookie'
import { csrfToken } from '@/api/auth'

/**
 * CSRF Token 管理服务
 * 提供全局的 CSRF token 管理功能
 */
class CsrfService {
  constructor() {
    this.token = null
    this.isInitializing = false
    this.initPromise = null
    this.retryCount = 0
    this.maxRetries = 3
    this.retryDelay = 1000 // 1秒
  }

  /**
   * 初始化 CSRF token
   * @returns {Promise<string|null>}
   */
  async initialize() {
    // 如果正在初始化，返回现有的 Promise
    if (this.isInitializing && this.initPromise) {
      return this.initPromise
    }

    // 首先尝试从 Cookie 中获取
    const cookieToken = Cookies.get('_csrf')
    if (cookieToken) {
      this.token = cookieToken
      return cookieToken
    }

    // 如果 Cookie 中没有，则从服务器获取
    this.isInitializing = true
    this.initPromise = this._fetchTokenWithRetry()
    
    try {
      const token = await this.initPromise
      this.token = token
      return token
    } catch (error) {
      console.warn('CSRF token 初始化失败:', error.message)
      return null
    } finally {
      this.isInitializing = false
      this.initPromise = null
    }
  }

  /**
   * 带重试机制的 token 获取
   * @returns {Promise<string>}
   */
  async _fetchTokenWithRetry() {
    for (let i = 0; i <= this.maxRetries; i++) {
      try {
        const response = await csrfToken()
        if (response && response.data && response.data.token) {
          const token = response.data.token
          // 将 token 存储到 Cookie 中
          Cookies.set('_csrf', token, { expires: 1 }) // 1天过期
          this.retryCount = 0 // 重置重试计数
          return token
        }
        throw new Error('Invalid token response')
      } catch (error) {
        console.warn(`CSRF token 获取失败 (尝试 ${i + 1}/${this.maxRetries + 1}):`, error.message)
        
        if (i < this.maxRetries) {
          // 等待后重试
          await new Promise(resolve => setTimeout(resolve, this.retryDelay * (i + 1)))
        } else {
          throw error
        }
      }
    }
  }

  /**
   * 获取当前的 CSRF token
   * @returns {string|null}
   */
  getToken() {
    // 优先返回内存中的 token
    if (this.token) {
      return this.token
    }
    
    // 其次从 Cookie 中获取
    const cookieToken = Cookies.get('_csrf')
    if (cookieToken) {
      this.token = cookieToken
      return cookieToken
    }
    
    return null
  }

  /**
   * 刷新 CSRF token
   * @returns {Promise<string|null>}
   */
  async refresh() {
    this.token = null
    Cookies.remove('_csrf')
    return this.initialize()
  }

  /**
   * 清除 CSRF token
   */
  clear() {
    this.token = null
    Cookies.remove('_csrf')
  }

  /**
   * 检查 token 是否有效
   * @returns {boolean}
   */
  isValid() {
    return !!this.getToken()
  }
}

// 创建全局实例
const csrfService = new CsrfService()

export default csrfService

// 导出便捷方法
export const initializeCsrf = () => csrfService.initialize()
export const getCsrfToken = () => csrfService.getToken()
export const refreshCsrfToken = () => csrfService.refresh()
export const clearCsrfToken = () => csrfService.clear()
export const isCsrfValid = () => csrfService.isValid()